import React, { FC, useEffect } from 'react';
import { TrackingProp } from 'react-tracking';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { PrimaryLoadingButton } from 'components';
import { analyticsWrapper } from 'analytics/itly/analyticsWrapper';
import { trackCheckoutStarted, trackCheckoutStepCompleted } from 'analytics/itly/tracking';
import { cartSelector, isAfterpayAppliedToCartSelector, placeOrderLoadingStateSelector } from 'store/cart/selectors';
import { afterpayCheckoutTypeSelector } from '../store/selectors';
import { openCustomerLoginModal } from 'store/modal/actions';
import { customerIsLoggedInSelector } from 'store/customer/selectors';
import { SmallLoader } from 'components/AULoader/SmallLoader';
import { isSuccess, isFetching } from 'store/constants';
import useAfterpay, { InitAfterpayCheckout } from '../hooks/useAfterpay';
import { useProductSLA } from '../../../hooks/useProductSLA';

export const AfterpayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  height: 4rem;
`;

export interface AfterpayHookProps {
  isAfterpayFetching: boolean;
  isAfterpayEnabled: boolean;
  isAfterpayAppliedToCart: boolean;
  isBillingAddressAppliedToCart: boolean;
  initAfterpayCheckout: ({ onCheckoutStart, onCheckoutEnd }: InitAfterpayCheckout) => Promise<void>;
  handleStartAfterpayFlow: () => void;
  handlePlaceOrderAfterpay: () => void;
}
export interface AfterpayButtonProps {
  tracking?: TrackingProp;
  isLoading?: boolean;
  disabled?: boolean;
}

const AfterpayButton: FC<AfterpayButtonProps> = ({ tracking, isLoading, disabled }) => {
  const dispatch = useDispatch();
  const showLoginModal = () => dispatch(openCustomerLoginModal());

  const {
    isAfterpayFetching,
    isAfterpayEnabled,
    initAfterpayCheckout,
    isBillingAddressAppliedToCart,
    handleStartAfterpayFlow,
    handlePlaceOrderAfterpay
  } = useAfterpay();

  const cart = useSelector(cartSelector);
  const isAfterpayAppliedToCart = useSelector(isAfterpayAppliedToCartSelector);
  const isLoggedIn = useSelector(customerIsLoggedInSelector);
  const placeOrderLoading = useSelector(placeOrderLoadingStateSelector);
  const afterpayCheckoutType = useSelector(afterpayCheckoutTypeSelector);
  const { getLongestSLAFromCart } = useProductSLA();
  const longestSLA = getLongestSLAFromCart(cart);

  // Returns a boolean indicating whenever it should continue with checkout or not
  const onCheckoutStart = () => {
    if (!isLoggedIn) {
      showLoginModal();
      return false;
    }
    trackCheckoutStarted(tracking, cart);
    trackCheckoutStepCompleted(tracking, cart.entity_id, 'cart', cart, longestSLA);
    return true;
  };

  const onCheckoutEnd = () => {
    console.log('checkout end');
  };

  useEffect(() => {
    console.log('use effect fire', {
      isAfterpayEnabled,
      isAfterpayAppliedToCart,
      afterpayCheckoutType,
      isBillingAddressAppliedToCart
    });
    if (afterpayCheckoutType === 'checkout') {
      initAfterpayCheckout({ onCheckoutStart, onCheckoutEnd });
    }
  // TODO: Refactor this dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAfterpayEnabled, isAfterpayAppliedToCart, afterpayCheckoutType, isBillingAddressAppliedToCart]);

  switch (afterpayCheckoutType) {
    case 'checkout':
      return (
        <SmallLoader isLoading={isAfterpayFetching}>
          <PrimaryLoadingButton
            isLoading={isAfterpayFetching}
            disabled={disabled}
            width="100%"
            onClick={handleStartAfterpayFlow}
          >
            Continue with Afterpay
          </PrimaryLoadingButton>
        </SmallLoader>
      );

    case 'pay':
      return (
        <AfterpayWrapper>
          <PrimaryLoadingButton
            id="Afterpay-button"
            width="100%"
            isLoading={isLoading || isFetching(placeOrderLoading)}
            disabled={disabled || isSuccess(placeOrderLoading)}
            onClick={handlePlaceOrderAfterpay}
          >
            Purchase with Afterpay
          </PrimaryLoadingButton>
        </AfterpayWrapper>
      );

    default:
      return null;
  }
};

export default analyticsWrapper(AfterpayButton);
